import { useContext, useCallback } from "react";
import { GlobalContext } from "../../../Context/GlobalContext";

const successResponse = (res, fallbackMessage = "Success") => ({
  success: true,
  data: res?.data?.response || res?.data?.data || {},
  message: res?.data?.responseMessage || res?.data?.message || fallbackMessage,
});

const failedResponse = (res, fallbackMessage = "Something went wrong") => ({
  success: false,
  data: res?.data?.response || res?.data?.data || {},
  message: res?.data?.responseMessage || res?.data?.message || fallbackMessage,
});

const catchResponse = (error, fallbackMessage = "Something went wrong") => ({
  success: false,
  data: {},
  message:
    error?.response?.data?.responseMessage ||
    error?.response?.data?.message ||
    error?.message ||
    fallbackMessage,
});

const uniqueBy = (arr = [], keyGetter) => {
  return Array.from(
    new Map(arr.map((item) => [keyGetter(item), item])).values(),
  );
};

const formatMySqlDateTime = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const rawValue = String(value).trim();

  const match = rawValue.match(
    /^(\d{4})[/-](\d{2})[/-](\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/,
  );

  if (!match) {
    return rawValue;
  }

  const [, year, month, day, hour = "00", minute = "00", second = "00"] = match;

  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
};

const normalizeExpiryDatePayload = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizeExpiryDatePayload);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => {
        if (key === "expiryDate" || key === "expiry_date") {
          return [key, formatMySqlDateTime(val)];
        }

        return [key, normalizeExpiryDatePayload(val)];
      }),
    );
  }

  return value;
};

export default function useCatalogue() {
  const { api, anlookApi } = useContext(GlobalContext);

  const updateCatalogueSizeId = async ({ usCode }) => {
    try {
      const res = await anlookApi.post("/Inventory/updateSizeIdItems", {
        usCode,
      });

      if (res?.data?.responseCode === "00") {
        return res?.data || {};
      }

      return (
        res?.data || {
          responseCode: "01",
          responseMessage: "Failed to update catalogue size id",
        }
      );
    } catch (error) {
      console.error("updateCatalogueSizeId error:", error);
      return {
        responseCode: "01",
        responseMessage: "Error updating size id",
      };
    }
  };

  const getAllInventoryVariant = async ({ userid }) => {
    try {
      const res = await api.post("/Inventory/v1.0/getAllInventoryVariant", {
        userid,
        secret: "string",
      });

      if (res?.data?.responseCode === "00") {
        const list = (res?.data?.response || []).map((r) => ({
          id: String(r.pk_variant_id ?? r.variantId ?? r._id ?? ""),
          pk_variant_id: r.pk_variant_id ?? r.variantId ?? r._id ?? "",
          variant_title: r.variant_title ?? r.variantTitle ?? "",
          category: r.category ?? "",
        }));

        return {
          success: true,
          data: list,
          message:
            res?.data?.responseMessage || "Variants fetched successfully",
        };
      }

      return failedResponse(res, "Failed to fetch variants");
    } catch (error) {
      console.error("getAllInventoryVariant error:", error);
      return catchResponse(error, "Error fetching variants");
    }
  };

  const enableDisablePackagingCharges = async ({ usCode, itemId, trigger }) => {
    try {
      const res = await api.post("/Order/EnableDisablePackagingCharges", {
        usCode: String(usCode),
        itemId: String(itemId),
        trigger: String(trigger),
      });

      if (res?.data?.responseCode === "00") {
        return successResponse(res, "Packaging charges updated successfully");
      }

      return failedResponse(res, "Failed to update packaging charges");
    } catch (error) {
      console.error("enableDisablePackagingCharges error:", error);
      return catchResponse(error, "Error updating packaging charges");
    }
  };

  const updateStocksAvailabilty = async ({ id, isVisible, isStock }) => {
    try {
      const res = await api.post("/Order/updateStocksAvailabilty", {
        id: String(id),
        isVisible: isVisible ? "Y" : "N",
        isStock: isStock ? "Y" : "N",
      });

      if (res?.data?.responseCode === "00") {
        return successResponse(res, "Variant updated successfully");
      }

      return failedResponse(res, "Failed to update");
    } catch (error) {
      console.error("updateStocksAvailabilty error:", error);
      return catchResponse(error, "Error updating variant");
    }
  };

  const updateItemAvailabiltyStatus = async ({ id, isVisible, isStock }) => {
    try {
      const res = await api.post("/Order/updateStocksAvailabilty", {
        id: String(id),
        isVisible,
        isStock,
      });

      if (res?.data?.responseCode === "00") {
        return successResponse(res, "Variant updated successfully");
      }

      return failedResponse(res, "Failed to update");
    } catch (error) {
      console.error("updateItemAvailabiltyStatus error:", error);
      return catchResponse(error, "Error updating variant");
    }
  };

  const getCatalogueItemsMaster = async ({ userId }) => {
    try {
      const res = await api.post("/Order/v1.0/getCatalogueItemsMaster", {
        userid: userId,
        secret: "string",
        status: "0",
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res?.data?.response || [],
          message: res?.data?.responseMessage || "Catalogue fetched",
        };
      }

      return {
        success: false,
        data: [],
        message: res?.data?.responseMessage || "Failed to fetch catalogue",
      };
    } catch (error) {
      console.error("getCatalogueItemsMaster error:", error);
      return {
        success: false,
        data: [],
        message: "Error fetching catalogue",
      };
    }
  };

  const updateStock = async ({ userId, id, inStock, type = "item" }) => {
    try {
      const res = await api.post("/Order/v1.0/updateStock", {
        userid: userId,
        id,
        stock: "1",
        inStock,
        type,
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          message: res?.data?.responseMessage || "Stock updated",
        };
      }

      return {
        success: false,
        message: res?.data?.responseMessage || "Failed to update stock",
      };
    } catch (error) {
      console.error("updateStock error:", error);
      return {
        success: false,
        message: "Error updating stock",
      };
    }
  };

  const getPackagingChargesAndTriggers = async ({ userId }) => {
    try {
      const res = await api.post("/Order/v1.0/getPackagingChargesAndTriggers", {
        userid: userId,
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res?.data?.response || [],
          message: res?.data?.responseMessage || "Packaging config fetched",
        };
      }

      return {
        success: false,
        data: [],
        message:
          res?.data?.responseMessage || "Failed to fetch packaging config",
      };
    } catch (error) {
      console.error("getPackagingChargesAndTriggers error:", error);
      return {
        success: false,
        data: [],
        message: "Error fetching packaging config",
      };
    }
  };

  const getRawInventoryMaster = async () => {
    try {
      const res = await api.post("/Inventory/v2.0/getRawInventoryMaster", {});

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res?.data?.response || [],
          message: res?.data?.responseMessage || "Raw inventory fetched",
        };
      }

      return {
        success: false,
        data: [],
        message: res?.data?.responseMessage || "Failed to load raw inventory",
      };
    } catch (error) {
      console.error("getRawInventoryMaster error:", error);
      return {
        success: false,
        data: [],
        message: "Error fetching raw inventory",
      };
    }
  };

  const insertRawInventoryMaster = async ({ itemName }) => {
    try {
      const res = await api.post("/Inventory/v2.0/insertRawInventoryMaster", {
        itemName,
      });

      if (res?.data?.responseCode === "00") {
        return successResponse(res, "Raw inventory added successfully");
      }

      return failedResponse(res, "Failed to add raw inventory");
    } catch (error) {
      console.error("insertRawInventoryMaster error:", error);
      return catchResponse(error, "Failed to add raw inventory");
    }
  };

  const insertCatalogItemVariant = async ({
    userId,
    itemId,
    price,
    variation,
    variationId = "",
  }) => {
    try {
      const res = await api.post("/Order/v1.0/InsertCatalogItemVariant", {
        userid: String(userId),
        itemId: String(itemId),
        price: String(price),
        variation,
        variationId,
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          message: res?.data?.responseMessage || "Variant added successfully",
        };
      }

      return {
        success: false,
        message: res?.data?.responseMessage || "Failed to add variant",
      };
    } catch (error) {
      console.error("insertCatalogItemVariant error:", error);
      return {
        success: false,
        message: "Something went wrong while adding variant",
      };
    }
  };

  const getCatalogueTableData = useCallback(
    async ({ fk_user_id, data_record_limit = 160, data_record_offset = 1 }) => {
      try {
        const res = await api.post("/Admin/catalogItemLimitOffsetData", {
          fk_user_id: Number(fk_user_id),
          data_record_limit: Number(data_record_limit),
          data_record_offset: Number(data_record_offset),
        });

        if (res?.data?.responseCode === "00") {
          return {
            success: true,
            data: Array.isArray(res?.data?.response) ? res.data.response : [],
            message: res?.data?.responseMessage || "Catalogue table fetched",
          };
        }

        return {
          success: false,
          data: [],
          message:
            res?.data?.responseMessage || "Failed to fetch catalogue table",
        };
      } catch (error) {
        console.error("getCatalogueTableData error:", error);
        return {
          success: false,
          data: [],
          message: "Error fetching catalogue table",
        };
      }
    },
    [api],
  );

  const getInventorySubCategories = async ({ userid, category = "" }) => {
    try {
      const res = await api.post("/Inventory/v1.0/getInventorySubCategories", {
        userid: String(userid),
        category: String(category),
      });

      if (res?.data?.responseCode === "00") {
        const uniqueData = uniqueBy(res.data.response || [], (item) => item.id);

        return {
          success: true,
          data: uniqueData,
          message: res?.data?.responseMessage || "Sub-categories fetched",
        };
      }

      return {
        success: false,
        data: [],
        message: res?.data?.responseMessage || "Failed to fetch sub-categories",
      };
    } catch (error) {
      console.error("getInventorySubCategories error:", error);
      return {
        success: false,
        data: [],
        message: "Error fetching sub-categories",
      };
    }
  };

  const getRackDetails = async ({ userid }) => {
    try {
      const res = await api.post("/Inventory/v1.0/getRackDetails", {
        userid: String(userid),
      });

      if (res?.data?.responseCode === "00") {
        const uniqueData = uniqueBy(
          res.data.response || [],
          (item) => item.rackNumber || item.id,
        );

        return {
          success: true,
          data: uniqueData,
          message: res?.data?.responseMessage || "Rack details fetched",
        };
      }

      return {
        success: false,
        data: [],
        message: res?.data?.responseMessage || "Failed to fetch rack details",
      };
    } catch (error) {
      console.error("getRackDetails error:", error);
      return {
        success: false,
        data: [],
        message: "Error fetching rack details",
      };
    }
  };

  const updateCatalogItems = async (payload) => {
    try {
      const normalizedPayload = normalizeExpiryDatePayload(payload);

      console.log("updateCatalogItems payload:", normalizedPayload);

      const res = await api.post(
        "/Order/v1.0/updateCatalogItems",
        normalizedPayload,
      );

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          message:
            res?.data?.responseMessage || "Catalog item updated successfully",
        };
      }

      return {
        success: false,
        message: res?.data?.responseMessage || "Failed to update catalog item",
      };
    } catch (error) {
      console.error(
        "updateCatalogItems error:",
        error?.response?.data || error,
      );

      return {
        success: false,
        message:
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          "Something went wrong while updating item",
      };
    }
  };

  const addRecipeIngredient = async (payload) => {
    try {
      const res = await api.post(
        "/Inventory/v1.0/addRecipeIngredient",
        payload,
      );

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          message:
            res?.data?.responseMessage || "Ingredients added successfully",
        };
      }

      return {
        success: false,
        message: res?.data?.responseMessage || "Failed to add ingredients",
      };
    } catch (error) {
      console.error("addRecipeIngredient error:", error);
      return {
        success: false,
        message: "Something went wrong while adding ingredients",
      };
    }
  };

  const getRecipeIngredient = async (payload) => {
    try {
      const res = await api.post(
        "/Inventory/v1.0/getRecipeIngredient",
        payload,
      );

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res?.data?.response || [],
        };
      }

      return {
        success: false,
        data: [],
      };
    } catch (error) {
      console.error("getRecipeIngredient error:", error);
      return {
        success: false,
        data: [],
      };
    }
  };

  const updateCatalogueTableItems = useCallback(
    async ({ requests = [] }) => {
      try {
        const res = await api.post("/Admin/updateCatalogItemRishabh", {
          requests,
        });

        if (res?.data?.status === "0") {
          return {
            success: true,
            data: res?.data?.result || res?.data || {},
            message: res?.data?.msg || "Catalogue items updated",
          };
        }

        return {
          success: false,
          data: res?.data || {},
          message: res?.data?.msg || "Failed to update catalogue items",
        };
      } catch (error) {
        console.error("updateCatalogueTableItems error:", error);
        return {
          success: false,
          data: {},
          message: "Error updating catalogue items",
        };
      }
    },
    [api],
  );

  const updateCatalogItemsVisibility = async ({ userId, itemId, status }) => {
    try {
      const res = await api.post("/Order/v1.0/updateCatalogItemsVisibility", {
        userid: userId,
        status,
        pkitemid: itemId,
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          message:
            res?.data?.responseMessage || "Visibility updated successfully",
        };
      }

      return {
        success: false,
        message: res?.data?.responseMessage || "Failed to update visibility",
      };
    } catch (error) {
      console.error("updateCatalogItemsVisibility error:", error);

      return {
        success: false,
        message: "Something went wrong",
      };
    }
  };

  const changeItemAvailability = async ({
    userid,
    itemId,
    homeDelivery,
    takeaway,
    dineIn,
  }) => {
    try {
      const res = await api.post("/Order/v1.0/changeItemAvailability", {
        userid,
        itemId,
        homeDelivery: !!homeDelivery,
        takeaway: !!takeaway,
        dining: !!dineIn,
      });

      if (res?.data?.responseCode === "00") {
        return successResponse(res, "Availability saved");
      }

      return failedResponse(res, "Failed to save availability");
    } catch (error) {
      console.error("changeItemAvailability error:", error);
      return catchResponse(error, "Error saving availability");
    }
  };

  const updateCategoryCustomization = async ({
    userid,
    categoryId,
    customizations,
  }) => {
    try {
      const res = await api.post("/Order/v1.0/updateCustomization", {
        userid,
        id: categoryId,
        customizations,
        type: "category",
        operation: "add",
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res?.data?.response || res?.data?.data || {},
          message:
            res?.data?.data?.message ||
            res?.data?.responseMessage ||
            "Customization updated",
        };
      }

      return failedResponse(res, "Failed to update customization");
    } catch (error) {
      console.error("updateCategoryCustomization error:", error);
      return catchResponse(error, "Error updating customization");
    }
  };

  const getPrinters = async ({ userid }) => {
    try {
      const res = await api.post("/Order/v1.0/getPrinters", {
        userid,
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res?.data?.response || [],
          message: res?.data?.responseMessage || "Printers fetched",
        };
      }

      return {
        success: false,
        data: [],
        message: res?.data?.responseMessage || "Failed to load printers",
      };
    } catch (error) {
      console.error("getPrinters error:", error);
      return {
        success: false,
        data: [],
        message: "Error fetching printers",
      };
    }
  };

  const savePrinterToItem = async ({
    userid,
    itemId,
    printerCode,
    printerAlias,
  }) => {
    try {
      const res = await api.post("/Order/v1.0/addPrinterCodeToItem", {
        userid,
        itemId,
        printerCode,
        printerAlias,
      });

      if (res?.data?.responseCode === "00") {
        return successResponse(res, "Printer saved");
      }

      return failedResponse(res, "Failed to save printer");
    } catch (error) {
      console.error("savePrinterToItem error:", error);
      return catchResponse(error, "Error saving printer");
    }
  };

  const getAdminUomMaster = async ({ sizeType = "0" } = {}) => {
    try {
      const res = await api.post("/Inventory/uomMasterGet", {
        size_type: String(sizeType || "0"),
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res?.data?.response || res?.data?.data || [],
          message: res?.data?.responseMessage || "UOM master fetched",
        };
      }

      return {
        success: false,
        data: [],
        message: res?.data?.responseMessage || "Failed to fetch UOM master",
      };
    } catch (error) {
      console.error("getAdminUomMaster error:", error);
      return {
        success: false,
        data: [],
        message: "Error fetching UOM master",
      };
    }
  };

  const getAdminSizeMaster = async ({ sizeType = "0" } = {}) => {
    try {
      const res = await api.post("/Inventory/sizeMasterGet", {
        size_type: String(sizeType || "0"),
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res?.data?.response || res?.data?.data || [],
          message: res?.data?.responseMessage || "Size master fetched",
        };
      }

      return {
        success: false,
        data: [],
        message: res?.data?.responseMessage || "Failed to fetch size master",
      };
    } catch (error) {
      console.error("getAdminSizeMaster error:", error);
      return {
        success: false,
        data: [],
        message: "Error fetching size master",
      };
    }
  };

  // GET CATALOGUE ITEM IMAGES

  const getCatalogueItemImages = async ({ fk_user_id }) => {
    try {
      const res = await api.post("/Order/getcatalogItemImage", {
        fk_user_id: String(fk_user_id),
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res?.data?.response || [],
          message: res?.data?.responseMessage || "Catalogue images fetched",
        };
      }

      return {
        success: false,
        data: [],
        message:
          res?.data?.responseMessage || "Failed to fetch catalogue images",
      };
    } catch (error) {
      console.error("getCatalogueItemImages error:", error);

      return {
        success: false,
        data: [],
        message: "Error fetching catalogue images",
      };
    }
  };

  // UPDATE CATALOGUE ITEM IMAGE

const updateCatalogueItemImage = async ({ pk_item_id, photo }) => {
  try {
    const payload = {
      pk_item_id: String(pk_item_id),
      photo: String(photo),
    };

    console.log("UPDATE CATALOGUE IMAGE PAYLOAD:", payload);

    const res = await api.post("/Order/updatecatalogItemImage", payload);

    console.log("UPDATE CATALOGUE IMAGE API RESPONSE:", res?.data);

    if (res?.data?.responseCode === "00") {
      return {
        success: true,
        data: res?.data?.response || res?.data || {},
        message:
          res?.data?.responseMessage || "Catalogue image updated successfully",
      };
    }

    return {
      success: false,
      data: res?.data || {},
      message:
        res?.data?.responseMessage ||
        res?.data?.message ||
        "Catalogue image update failed",
    };
  } catch (error) {
    console.error(
      "updateCatalogueItemImage error:",
      error?.response?.data || error,
    );

    return {
      success: false,
      data: error?.response?.data || {},
      message:
        error?.response?.data?.responseMessage ||
        error?.response?.data?.message ||
        error?.message ||
        "Error updating catalogue image",
    };
  }
};

  const uploadAdminItemImage = async ({ 
    userid,
    item_name,
    image_data,
    type = "IMG",
  }) => {
    try {
      if (!anlookApi) {
        return {
          success: false,
          data: {},
          message: "Anlook API instance missing",
        };
      }

      if (!userid) {
        return {
          success: false,
          data: {},
          message: "User id missing",
        };
      }

      const res = await anlookApi.post("/v1/uploadItemImagev2.php", {
        userid: String(userid),
        item_name,
        type,
        image_data,
      });

      if (String(res?.data?.status) === "0" && res?.data?.image_url) {
        return {
          success: true,
          data: res.data,
          message: res?.data?.message || "Image uploaded successfully",
        };
      }

      return {
        success: false,
        data: res?.data || {},
        message:
          res?.data?.message ||
          res?.data?.responseMessage ||
          "Image upload failed",
      };
    } catch (error) {
      console.error(
        "uploadAdminItemImage error:",
        error?.response?.data || error,
      );

      return {
        success: false,
        data: error?.response?.data || {},
        message:
          error?.response?.data?.message ||
          error?.response?.data?.responseMessage ||
          error?.message ||
          "Error uploading image",
      };
    }
  };

  const insertAdminCatalogItem = async ({ userid, items }) => {
    try {
      const res = await api.post("/Order/v1.0/insertCatalogItem", {
        userid: String(userid),
        items: Array.isArray(items) ? items : [],
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res?.data?.response || {},
          message: res?.data?.responseMessage || "Item added successfully",
        };
      }

      return {
        success: false,
        data: res?.data?.response || {},
        message: res?.data?.responseMessage || "Failed to add item",
      };
    } catch (error) {
      console.error("insertAdminCatalogItem error:", error);
      return {
        success: false,
        data: {},
        message: "Error saving item",
      };
    }
  };

  const extractAdminChaafoMenuFromImage = async (file) => {
    try {
      if (!file) {
        return {
          success: false,
          data: null,
          message: "Please select image",
        };
      }

      const formData = new FormData();
      formData.append("image", file);

      const res = await api.post("/Chaafo/extract", formData, {
        headers: {
          Accept: "*/*",
        },
      });

      if (res?.data?.status === "completed") {
        return {
          success: true,
          data: res.data,
          message: "Image extracted successfully",
        };
      }

      return {
        success: false,
        data: null,
        message:
          res?.data?.error || res?.data?.message || "Failed to extract image",
      };
    } catch (error) {
      console.error("extractAdminChaafoMenuFromImage error:", error);

      return {
        success: false,
        data: null,
        message:
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          error?.message ||
          "Error extracting image",
      };
    }
  };

   const searchCatalogueItems = async ({ text }) => {
  try {
    const response = await api.post(
      "/Admin/v1.0/catalogItemData",
      {
        text,
      },
      {
        headers: {
          "Content-Type": "application/json-patch+json",
          accept: "*/*",
        },
      }
    );

    return {
      success: response?.data?.responseCode === "00",
      data: Array.isArray(response?.data?.response)
        ? response.data.response
        : [],
      message: response?.data?.responseMessage,
    };
  } catch (error) {
    console.error("Search catalogue API error:", error);

    return {
      success: false,
      data: [],
      message:
        error?.response?.data?.responseMessage ||
        error?.message ||
        "Failed to search catalogue",
    };
  }
};

  return {
    updateCatalogueSizeId,
    getAllInventoryVariant,
    enableDisablePackagingCharges,
    updateStocksAvailabilty,
    updateItemAvailabiltyStatus,
    getCatalogueItemsMaster,
    updateStock,
    getPackagingChargesAndTriggers,
    getRawInventoryMaster,
    insertRawInventoryMaster,
    insertCatalogItemVariant,
    getCatalogueTableData,
    getInventorySubCategories,
    getRackDetails,
    updateCatalogItems,
    addRecipeIngredient,
    getRecipeIngredient,
    updateCatalogueTableItems,
    updateCatalogItemsVisibility,
    changeItemAvailability,
    updateCategoryCustomization,
    getPrinters,
    savePrinterToItem,
    getAdminUomMaster,
    getAdminSizeMaster,
    uploadAdminItemImage,
    insertAdminCatalogItem,
    extractAdminChaafoMenuFromImage,
    getCatalogueItemImages,
    updateCatalogueItemImage,
    searchCatalogueItems,
  };
}
